(() => {
  const MONTHS = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ];

  const MONTH_PATTERN = new RegExp(`\\b(${MONTHS.join('|')})\\b`, 'i');
  const NAME_PATTERN = /(?:her\s*)?name|personal(?:ized|isation|ization)?|write\s+the\s+text|custom\s*text/i;
  const EXCLUDED_TEXT_INPUT = /search|email|phone|quantity|discount|coupon|note/i;

  // Great Vibes capitals do not all begin at the same visual position. These
  // small corrections pull the flower stem into the first usable stroke of
  // the capital, matching the welded construction of the finished necklace.
  const CAPITAL_JOIN_OFFSETS = {
    A: -0.55,
    B: -0.4,
    D: -0.6,
    Q: -0.1,
    S: -0.6,
    U: -0.1,
    V: -0.1,
    W: -0.1,
    X: -0.7,
    Y: -0.1,
  };

  const elementDescription = (element) => {
    if (!element) return '';

    const labels = element.labels ? Array.from(element.labels).map((label) => label.textContent) : [];
    const parentText = element.closest('label, fieldset, [class*="option" i], [class*="easify" i]')?.textContent || '';
    const selectedText = element.tagName === 'SELECT' ? element.selectedOptions?.[0]?.textContent || '' : '';

    return [
      element.name,
      element.id,
      element.value,
      element.placeholder,
      element.getAttribute('aria-label'),
      element.getAttribute('data-value'),
      element.getAttribute('alt'),
      selectedText,
      labels.join(' '),
      parentText.slice(0, 240),
    ]
      .filter(Boolean)
      .join(' ');
  };

  const monthFromText = (text) => {
    const match = String(text || '').match(MONTH_PATTERN);
    return match ? match[1].toLowerCase() : null;
  };

  const finishFromText = (text) => {
    const value = String(text || '').toLowerCase();
    if (/yellow\s*gold|18\s*k|gold\s*finish|\bgold\b/.test(value)) return 'gold';
    if (/stainless|polished|silver|rhodium/.test(value)) return 'silver';
    return null;
  };

  const findNameInput = (root) => {
    const inputs = Array.from(root.querySelectorAll('input[type="text"], textarea'));
    let bestInput = null;
    let bestScore = -1;

    inputs.forEach((input) => {
      if (input.closest('[data-birth-flower-preview]')) return;

      const description = elementDescription(input);
      if (EXCLUDED_TEXT_INPUT.test(description) && !NAME_PATTERN.test(description)) return;

      let score = 0;
      if (NAME_PATTERN.test(description)) score += 10;
      if (input.maxLength > 0 && input.maxLength <= 12) score += 4;
      if (/elizabeth|sofia|your name|mom/i.test(input.placeholder || '')) score += 4;
      if (/properties\[/i.test(input.name || '')) score += 1;

      if (score > bestScore) {
        bestInput = input;
        bestScore = score;
      }
    });

    return bestScore > 0 ? bestInput : null;
  };

  const selectedControls = (root) => [
    ...root.querySelectorAll('input:checked, select, [role="radio"][aria-checked="true"], [aria-selected="true"]'),
  ];

  const readSelectedMonth = (root) => {
    for (const control of selectedControls(root)) {
      const month = monthFromText(elementDescription(control));
      if (month) return month;
    }
    return null;
  };

  const readSelectedFinish = (root) => {
    for (const control of selectedControls(root)) {
      const finish = finishFromText(elementDescription(control));
      if (finish) return finish;
    }
    return null;
  };

  const styleOptionHeadings = (root) => {
    [
      /^1\.\s*finish\s*\*?$/i,
      /^2\.\s*box\s+choice\s*\*?$/i,
      /^3\.\s*her\s+name\s*\*?$/i,
      /^4\.\s*month\s*\*?$/i,
    ].forEach((headingPattern) => {
      const matches = Array.from(root.querySelectorAll('label, legend, p, div, span, strong')).filter(
        (element) => {
          const headingText = String(element.textContent || '')
            .replace('(up to 11 characters)', '')
            .replace(/\s+/g, ' ')
            .trim();
          return !element.closest('[data-birth-flower-preview]') && headingPattern.test(headingText);
        },
      );
      const target = matches.find(
        (element) => !matches.some((other) => other !== element && element.contains(other)),
      );

      if (target) target.classList.add('birth-flower-preview__option-heading');
    });
  };

  const ensureNameLimitHint = (root, input, previewId) => {
    if (!input) return;

    const limitTextPattern = /^\(?\s*up\s+to\s+11\s+characters\s*\)?$/i;

    let hint = root.querySelector('[data-birth-flower-name-limit]');
    if (!hint) {
      hint = document.createElement('span');
      hint.className = 'birth-flower-preview__name-limit';
      hint.dataset.birthFlowerNameLimit = '';
      hint.id = `${previewId || 'BirthFlowerPreview'}-NameLimit`;
      hint.textContent = '(up to 11 characters)';
    }

    const explicitLabel = input.id
      ? Array.from(root.querySelectorAll('label[for]')).find((label) => label.htmlFor === input.id)
      : null;
    const headingCandidates = Array.from(
      root.querySelectorAll('label, legend, h1, h2, h3, h4, p, div, span, strong'),
    )
      .filter((element) => !element.contains(input) && !element.closest('[data-birth-flower-preview]'))
      .map((element) => {
        const textWithoutHint = String(element.textContent || '')
          .replace('(up to 11 characters)', '')
          .replace(/\s+/g, ' ')
          .trim();
        const isNameHeading = /^(?:\d+\s*[.)-]?\s*)?(?:her\s*)?name\s*\*?$/i.test(textWithoutHint);
        const score =
          (isNameHeading ? 10 : 0) +
          (/^\d/.test(textWithoutHint) ? 5 : 0) +
          (/\*/.test(textWithoutHint) ? 3 : 0) +
          (element === explicitLabel ? 1 : 0);
        return { element, score, textLength: textWithoutHint.length };
      })
      .filter((candidate) => candidate.score >= 10)
      .sort((a, b) => b.score - a.score || a.textLength - b.textLength);
    const label = headingCandidates[0]?.element || explicitLabel || input.labels?.[0];

    if (label) {
      hint.classList.remove('birth-flower-preview__name-limit--standalone');
      if (!label.contains(hint)) label.append(hint);
    } else if (!hint.isConnected || hint.nextElementSibling !== input) {
      hint.classList.add('birth-flower-preview__name-limit--standalone');
      input.insertAdjacentElement('beforebegin', hint);
    }

    Array.from(root.querySelectorAll('p, div, span, small'))
      .filter((element) => element !== hint && !element.contains(hint))
      .filter((element) => limitTextPattern.test(String(element.textContent || '').replace(/\s+/g, ' ').trim()))
      .forEach((element) => {
        element.hidden = true;
        element.setAttribute('aria-hidden', 'true');
      });

    const describedBy = new Set((input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    describedBy.add(hint.id);
    input.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
  };

  const initPreview = (preview) => {
    if (preview.dataset.previewReady === 'true') return;
    preview.dataset.previewReady = 'true';

    const productRoot = preview.closest('.product, .product__info-container, product-info') || document;
    const stage = preview.querySelector('[data-preview-stage]');
    const flower = preview.querySelector('[data-preview-flower]');
    const nameElement = preview.querySelector('[data-preview-name]');

    if (!stage || !flower || !nameElement) return;

    const state = {
      name: '',
      month: 'january',
      finish: 'silver',
    };

    const render = () => {
      const displayName = state.name.trim().slice(0, 11) || 'Name';
      const imageUrl = preview.dataset[state.month] || preview.dataset.january;
      const nameLength = displayName.length;
      const firstLetter = (displayName.match(/[a-z]/i)?.[0] || '').toUpperCase();
      const joinOffset = CAPITAL_JOIN_OFFSETS[firstLetter] || 0;

      flower.src = imageUrl;
      flower.dataset.month = state.month;
      const initial = document.createElement('span');
      const remainder = document.createElement('span');

      initial.className = 'birth-flower-preview__initial';
      initial.textContent = displayName.slice(0, 1);
      remainder.className = 'birth-flower-preview__remainder';
      remainder.textContent = displayName.slice(1);

      if (firstLetter === 'A' && preview.dataset.capitalA) {
        initial.classList.add('birth-flower-preview__initial--supplier-a');
        initial.style.setProperty('--capital-a-mask', `url("${preview.dataset.capitalA}")`);
      }

      nameElement.replaceChildren(initial, remainder);
      nameElement.style.fontSize = `${nameLength >= 10 ? 4.5 : nameLength >= 8 ? 5.1 : nameLength >= 6 ? 5.7 : 6.2}rem`;
      nameElement.style.marginLeft = `${joinOffset}rem`;
      nameElement.dataset.firstLetter = firstLetter;

      stage.classList.toggle('birth-flower-preview__stage--gold', state.finish === 'gold');
      stage.classList.toggle('birth-flower-preview__stage--silver', state.finish !== 'gold');
      stage.setAttribute(
        'aria-label',
        `${state.finish === 'gold' ? 'Gold' : 'Silver'} ${state.month} birth flower necklace preview with the name ${displayName}`,
      );
    };

    const syncFromForm = () => {
      styleOptionHeadings(productRoot);

      const nameInput = findNameInput(productRoot);
      const selectedMonth = readSelectedMonth(productRoot);
      const selectedFinish = readSelectedFinish(productRoot);

      if (nameInput) {
        state.name = nameInput.value || '';
        ensureNameLimitHint(productRoot, nameInput, preview.id);

        let anchor = nameInput;
        let candidate = nameInput.parentElement;

        while (candidate && candidate !== productRoot && !candidate.contains(preview)) {
          const controls = candidate.querySelectorAll('input, textarea, select').length;
          const containsMonth = Boolean(monthFromText(candidate.textContent));

          if (controls > 2 || containsMonth) break;
          anchor = candidate;
          candidate = candidate.parentElement;
        }

        if (anchor.nextElementSibling !== preview) {
          anchor.insertAdjacentElement('afterend', preview);
        }
      }
      if (selectedMonth) state.month = selectedMonth;
      if (selectedFinish) state.finish = selectedFinish;
      render();
    };

    productRoot.addEventListener('input', (event) => {
      const nameInput = findNameInput(productRoot);
      if (nameInput && event.target === nameInput) {
        state.name = nameInput.value || '';
        render();
      }
    });

    productRoot.addEventListener('change', () => {
      window.requestAnimationFrame(syncFromForm);
    });

    productRoot.addEventListener('click', (event) => {
      const choice = event.target.closest(
        'label, button, [role="radio"], [data-value], [class*="option" i], [class*="swatch" i]',
      );
      const description = elementDescription(choice || event.target);
      const clickedMonth = monthFromText(description);
      const clickedFinish = finishFromText(description);

      if (clickedMonth) state.month = clickedMonth;
      if (clickedFinish) state.finish = clickedFinish;
      if (clickedMonth || clickedFinish) render();

      window.setTimeout(syncFromForm, 0);
      window.setTimeout(syncFromForm, 120);
    });

    document.addEventListener('variant:change', () => window.requestAnimationFrame(syncFromForm));

    let observerTimer;
    const observer = new MutationObserver(() => {
      window.clearTimeout(observerTimer);
      observerTimer = window.setTimeout(syncFromForm, 80);
    });
    observer.observe(productRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked', 'selected'] });

    syncFromForm();
    window.setTimeout(syncFromForm, 500);
    window.setTimeout(syncFromForm, 1500);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-birth-flower-preview]').forEach(initPreview);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
